import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';

const STYLES = {
  success: { bg: '#ffffff', border: '#86EFAC', color: '#166534', Icon: CheckCircleOutlined, iconColor: '#22C55E' },
  error:   { bg: '#ffffff', border: '#FCA5A5', color: '#991B1B', Icon: CloseCircleOutlined, iconColor: '#EF4444' },
  warning: { bg: '#ffffff', border: '#FCD34D', color: '#92400E', Icon: ExclamationCircleOutlined, iconColor: '#F59E0B' },
  info:    { bg: '#ffffff', border: '#93C5FD', color: '#1E40AF', Icon: InfoCircleOutlined, iconColor: '#3B82F6' },
};

const ANIMATION_DURATION = 320;

function ToastItem({ id, message, type = 'info', onRemove }) {
  const { bg, border, color, Icon, iconColor } = STYLES[type] ?? STYLES.info;
  const [leaving, setLeaving] = useState(false);
  const timer = useRef(null);

  function dismiss() {
    setLeaving(true);
    setTimeout(() => onRemove(id), ANIMATION_DURATION);
  }

  useEffect(() => {
    timer.current = setTimeout(dismiss, 3500);
    return () => clearTimeout(timer.current);
  }, []);

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: bg, border: `1px solid ${border}`, borderRadius: 4,
      padding: '12px 14px', minWidth: 280, maxWidth: 380,
      boxShadow: '0 4px 12px rgba(0,0,0,0.10)', color,
      animation: `${leaving ? 'toast-out' : 'toast-in'} ${ANIMATION_DURATION}ms ease forwards`,
    }}>
      <Icon style={{ fontSize: 17, color: iconColor, marginTop: 1, flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: 13, lineHeight: 1.5 }}>{message}</span>
      <button onClick={dismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color, opacity: 0.6, lineHeight: 1 }}>
        <CloseOutlined style={{ fontSize: 12 }} />
      </button>
    </div>
  );
}

export default function Toast({ toasts, onRemove }) {
  if (!toasts.length) return null;

  return createPortal(
    <>
      <style>{`
        @keyframes toast-in  { from { opacity:0; transform:translateX(110%); } to   { opacity:1; transform:translateX(0);    } }
        @keyframes toast-out { from { opacity:1; transform:translateX(0);    } to   { opacity:0; transform:translateX(110%); } }
      `}</style>
      <div style={{ position: 'fixed', top: 55, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {toasts.map((t) => <ToastItem key={t.id} {...t} onRemove={onRemove} />)}
      </div>
    </>,
    document.body
  );
}
